"use client";

import { useEffect, useState } from "react";
import Link from "@/components/LocaleLink";
import { useTranslation } from "@/lib/i18n/LanguageContext";
import { GA4_ID } from "@/lib/config";
import { CONSENT_REOPEN_KEY, PRIVATE_AREA, hasConsentDecision, setConsent } from "@/lib/consent";
import { useSitePathname } from "@/lib/i18n/useSitePathname";
import { cameFromAd } from "@/lib/adClick";

// Opt-in for Google Ads conversion measurement (operator, 06.10.2026) and,
// once a GA4 property is set up, Google Analytics statistics (11.10.2026):
// the Google tag loads only after "accept" (TDDDG § 25, Art. 6 (1) (a) GDPR).
// "Reject" is as prominent as "accept"; "choose" lets each purpose be picked
// alone, nothing pre-ticked. Everything else stays strictly necessary
// (language choice, portal session). The wording asks plainly and names
// purpose, Google and that the choice can be changed (operator, 11.10.2026:
// more visitors should agree, legally clean).
const COPY = {
  de: {
    title: "Cookies",
    body: "Dürfen wir Cookies nutzen? Mit eurer Zustimmung zeigen uns Google Analytics und Google Ads, wie Paare uns finden und wo unsere Seite noch hakt. Keine personalisierte Werbung. Ihr könnt das jederzeit ändern.",
    bodyAds: "Dürfen wir Cookies nutzen? Mit eurer Zustimmung zeigt uns Google Ads, welche Anzeigen Paare zu uns führen. Keine personalisierte Werbung. Ihr könnt das jederzeit ändern.",
    accept: "Akzeptieren",
    reject: "Ablehnen",
    choose: "Auswählen",
    save: "Auswahl speichern",
    more: "Mehr erfahren",
    stats: ["Statistik (Google Analytics)", "Wie die Seite genutzt wird"],
    ads: ["Werbemessung (Google Ads)", "Welche Anzeige zu uns geführt hat"],
    necessary: "Notwendig: Sprache und Login, immer aktiv",
  },
  en: {
    title: "Cookies",
    body: "May we use cookies? With your consent, Google Analytics and Google Ads show us how couples find us and where our site still falls short. No personalised ads. You can change this at any time.",
    bodyAds: "May we use cookies? With your consent, Google Ads shows us which ads bring couples to us. No personalised ads. You can change this at any time.",
    accept: "Accept",
    reject: "Reject",
    choose: "Choose",
    save: "Save choice",
    more: "Learn more",
    stats: ["Statistics (Google Analytics)", "How the site is used"],
    ads: ["Ad measurement (Google Ads)", "Which ad brought you here"],
    necessary: "Necessary: language and login, always on",
  },
  da: {
    title: "Cookies",
    body: "Må vi bruge cookies? Med jeres samtykke viser Google Analytics og Google Ads os, hvordan par finder os, og hvor vores side stadig kan blive bedre. Ingen personaliserede annoncer. I kan altid ændre det.",
    bodyAds: "Må vi bruge cookies? Med jeres samtykke viser Google Ads os, hvilke annoncer der fører par til os. Ingen personaliserede annoncer. I kan altid ændre det.",
    accept: "Accepter",
    reject: "Afvis",
    choose: "Vælg",
    save: "Gem valg",
    more: "Læs mere",
    stats: ["Statistik (Google Analytics)", "Hvordan siden bruges"],
    ads: ["Annoncemåling (Google Ads)", "Hvilken annonce der førte jer hertil"],
    necessary: "Nødvendige: sprog og login, altid aktive",
  },
  ar: {
    title: "ملفات تعريف الارتباط",
    body: "هل تسمحون لنا باستخدام ملفات تعريف الارتباط؟ بموافقتكم تُظهر لنا Google Analytics وGoogle Ads كيف يجدنا الأزواج وأين يمكن تحسين موقعنا. لا إعلانات مخصّصة. يمكنكم تغيير ذلك في أي وقت.",
    bodyAds: "هل تسمحون لنا باستخدام ملفات تعريف الارتباط؟ بموافقتكم تُظهر لنا Google Ads أي الإعلانات توصل الأزواج إلينا. لا إعلانات مخصّصة. يمكنكم تغيير ذلك في أي وقت.",
    accept: "موافقة",
    reject: "رفض",
    choose: "اختيار",
    save: "حفظ الاختيار",
    more: "المزيد",
    stats: ["الإحصاءات (Google Analytics)", "كيف يُستخدم الموقع"],
    ads: ["قياس الإعلانات (Google Ads)", "أي إعلان أوصلكم إلينا"],
    necessary: "الضرورية: اللغة وتسجيل الدخول، مفعّلة دائمًا",
  },
  uk: {
    title: "Файли cookie",
    body: "Чи можна нам використовувати файли cookie? За вашою згодою Google Analytics і Google Ads показують нам, як пари нас знаходять і що на сайті варто покращити. Без персоналізованої реклами. Це можна змінити будь-коли.",
    bodyAds: "Чи можна нам використовувати файли cookie? За вашою згодою Google Ads показує нам, яка реклама приводить до нас пари. Без персоналізованої реклами. Це можна змінити будь-коли.",
    accept: "Прийняти",
    reject: "Відхилити",
    choose: "Вибрати",
    save: "Зберегти вибір",
    more: "Докладніше",
    stats: ["Статистика (Google Analytics)", "Як використовують сайт"],
    ads: ["Вимірювання реклами (Google Ads)", "Яка реклама привела вас сюди"],
    necessary: "Необхідні: мова та вхід, завжди активні",
  },
  ru: {
    title: "Файлы cookie",
    body: "Можно нам использовать файлы cookie? С вашего согласия Google Analytics и Google Ads показывают нам, как пары нас находят и что на сайте стоит улучшить. Без персонализированной рекламы. Это можно изменить в любой момент.",
    bodyAds: "Можно нам использовать файлы cookie? С вашего согласия Google Ads показывает нам, какая реклама приводит к нам пары. Без персонализированной рекламы. Это можно изменить в любой момент.",
    accept: "Принять",
    reject: "Отклонить",
    choose: "Выбрать",
    save: "Сохранить выбор",
    more: "Подробнее",
    stats: ["Статистика (Google Analytics)", "Как используется сайт"],
    ads: ["Измерение рекламы (Google Ads)", "Какая реклама привела вас сюда"],
    necessary: "Необходимые: язык и вход, всегда активны",
  },
  vi: {
    title: "Cookie",
    body: "Chúng tôi có thể dùng cookie không? Khi các bạn đồng ý, Google Analytics và Google Ads cho chúng tôi biết các cặp đôi tìm đến chúng tôi như thế nào và trang web cần cải thiện ở đâu. Không có quảng cáo cá nhân hóa. Các bạn có thể thay đổi bất cứ lúc nào.",
    bodyAds: "Chúng tôi có thể dùng cookie không? Khi các bạn đồng ý, Google Ads cho chúng tôi biết quảng cáo nào đưa các cặp đôi đến với chúng tôi. Không có quảng cáo cá nhân hóa. Các bạn có thể thay đổi bất cứ lúc nào.",
    accept: "Đồng ý",
    reject: "Từ chối",
    choose: "Chọn",
    save: "Lưu lựa chọn",
    more: "Tìm hiểu thêm",
    stats: ["Thống kê (Google Analytics)", "Cách trang web được sử dụng"],
    ads: ["Đo lường quảng cáo (Google Ads)", "Quảng cáo nào đưa các bạn đến đây"],
    necessary: "Cần thiết: ngôn ngữ và đăng nhập, luôn bật",
  },
  es: {
    title: "Cookies",
    body: "¿Podemos usar cookies? Con su consentimiento, Google Analytics y Google Ads nos muestran cómo nos encuentran las parejas y en qué puntos nuestra web aún falla. Sin publicidad personalizada. Pueden cambiarlo en cualquier momento.",
    bodyAds: "¿Podemos usar cookies? Con su consentimiento, Google Ads nos muestra qué anuncios traen a las parejas hasta nosotros. Sin publicidad personalizada. Pueden cambiarlo en cualquier momento.",
    accept: "Aceptar",
    reject: "Rechazar",
    choose: "Elegir",
    save: "Guardar selección",
    more: "Más información",
    stats: ["Estadísticas (Google Analytics)", "Cómo se usa la web"],
    ads: ["Medición de anuncios (Google Ads)", "Qué anuncio los trajo hasta nosotros"],
    necessary: "Necesarias: idioma e inicio de sesión, siempre activas",
  },
} as const;

const BUTTON =
  "inline-flex h-auto min-h-11 min-w-0 flex-1 items-center justify-center rounded-full border border-navy-200 bg-white px-3 py-1.5 text-center text-[13.5px] leading-tight font-semibold text-ink-900 transition-colors hover:border-navy-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral-400";

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  // Shown once the visitor reads on (half a screen of scrolling): at the top
  // of the page it sat on the hero's call to action (journey test
  // 04.10.2026). No extra waiting time any more (operator, 11.10.2026).
  const [readingOn, setReadingOn] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [pick, setPick] = useState({ stats: false, ads: false });
  const { locale } = useTranslation();
  const pathname = useSitePathname();
  const copy = COPY[locale] || COPY.de;
  const withStats = Boolean(GA4_ID);

  useEffect(() => {
    try {
      // Opened on purpose via "Cookie-Einstellungen", or a visitor from a
      // Google ad (only their consent lets the ad be credited, and many leave
      // within seconds): show at once.
      // Google Tag Assistant opens the page with ?gtm_debug=…: show at once too, so a test can accept and see the tag.
      const tagDebug = /[?&]gtm_debug=/.test(window.location.search);
      const now = sessionStorage.getItem(CONSENT_REOPEN_KEY) || (!hasConsentDecision() && (cameFromAd() || tagDebug));
      if (now || !hasConsentDecision()) {
        const t = setTimeout(() => {
          if (now) setReadingOn(true);
          setVisible(true);
        }, 0);
        return () => clearTimeout(t);
      }
    } catch {
      /* SSR or private mode */
    }
  }, []);

  useEffect(() => {
    if (!visible || readingOn) return;
    const check = () => {
      if (window.scrollY > window.innerHeight * 0.5) setReadingOn(true);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    return () => window.removeEventListener("scroll", check);
  }, [visible, readingOn]);

  function decide(choice: { ads: "granted" | "denied"; stats: "granted" | "denied" }) {
    setVisible(false);
    try {
      sessionStorage.removeItem(CONSENT_REOPEN_KEY);
    } catch {
      /* private mode */
    }
    setConsent(choice);
  }
  const all = (v: "granted" | "denied") => decide({ ads: v, stats: v });

  // Nothing to consent to in the portal: the Google tag never loads there.
  if (!visible || !readingOn || PRIVATE_AREA.test(pathname ?? "")) return null;

  return (
    <div
      role="dialog"
      aria-label={copy.title}
      className="fixed start-3 end-[4.75rem] bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-[55] transition-[bottom] duration-300 max-sm:[.dock-up_&]:bottom-[4.75rem] sm:end-auto sm:bottom-5 sm:start-5 sm:w-full sm:max-w-[25rem]"
      style={{ animation: "var(--animate-fade-up)" }}
    >
      {/* A small card in the bottom start corner, beside the contact bubble
          in the end corner, so it never covers form fields; above the
          ConversionDock while the dock is up (.dock-up on <html>). z-[55]:
          above the header and the dock, below the open phone menu (z-[60]).
          White with a border, so it never melts into the dark footer. */}
      <div className="rounded-2xl border border-navy-100 bg-white px-4 py-3.5 text-ink-900 shadow-[var(--shadow-card)]">
        {choosing ? (
          <fieldset className="space-y-2.5">
            <legend className="sr-only">{copy.choose}</legend>
            {(withStats ? (["stats", "ads"] as const) : (["ads"] as const)).map((k) => (
              <label key={k} className="flex cursor-pointer items-start gap-2.5 text-[13px] leading-snug">
                <input
                  type="checkbox"
                  checked={pick[k]}
                  onChange={(e) => setPick((p) => ({ ...p, [k]: e.target.checked }))}
                  className="mt-0.5 h-[18px] w-[18px] shrink-0 accent-coral-600"
                />
                <span>
                  <span className="block font-semibold text-ink-900">{copy[k][0]}</span>
                  <span className="block text-navy-600">{copy[k][1]}</span>
                </span>
              </label>
            ))}
            <p className="text-[12px] leading-snug text-navy-500">{copy.necessary}</p>
          </fieldset>
        ) : (
          <p className="text-[13px] leading-relaxed text-navy-700">
            {withStats ? copy.body : copy.bodyAds}{" "}
            <Link href="/datenschutz" className="font-semibold text-coral-700 underline underline-offset-2 hover:text-coral-800">
              {copy.more}
            </Link>
          </p>
        )}
        {/* Reject and accept in the same size and style: no nudging. */}
        <div className="mt-2.5 flex gap-2">
          {choosing ? (
            <>
              <button
                type="button"
                className={BUTTON}
                onClick={() => decide({ ads: pick.ads ? "granted" : "denied", stats: pick.stats ? "granted" : "denied" })}
              >
                {copy.save}
              </button>
              <button type="button" className={BUTTON} onClick={() => all("granted")}>
                {copy.accept}
              </button>
            </>
          ) : (
            <>
              <button type="button" className={BUTTON} onClick={() => all("denied")}>
                {copy.reject}
              </button>
              <button type="button" className={BUTTON} onClick={() => all("granted")}>
                {copy.accept}
              </button>
            </>
          )}
        </div>
        {!choosing && (
          <button
            type="button"
            onClick={() => setChoosing(true)}
            className="mt-2 block w-full py-1 text-center text-[12.5px] font-semibold text-navy-600 underline-offset-2 hover:text-coral-700 hover:underline"
          >
            {copy.choose}
          </button>
        )}
      </div>
    </div>
  );
}
